import { useEffect, useMemo, useState } from 'react';

import { PolicyDeniedView } from '@/components/common/PolicyDeniedView';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { normalizeApiError, mutationErrorMessage } from '@/features/common/apiError';
import { useGetFactoriesQuery } from '@/features/agents/api/agentsApi';
import { BoundAgentNotice } from './BoundAgentNotice';
import { useBindNumberMutation } from '../api/numbersApi';
import { groupFactoriesByVoice } from '../utils';
import type { FactoryWithForwarding, PhoneNumberView } from '../types';

export interface BindAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  number: PhoneNumberView;
}

/**
 * Bind-to-agent dialog (design §9). `POST /v1/voice-bridge/numbers/:id/bind`.
 * The bind OVERWRITES an existing binding (numbers.service.ts:176-177), so
 * every surface that offers a bound number discloses the move (audit #1): the
 * current binding is named, cross-target selection swaps the primary label to
 * "Move number" and raises the amber notice. Grouping is computed from
 * `forwarding.enabled` — never a kind hardcode (audit #5). Factories come from
 * the reused P2 `getFactories({ orgId })` query (§15 H1).
 */
export function BindAgentDialog({ open, onOpenChange, orgId, number }: BindAgentDialogProps) {
  const toast = useToast();
  const [bindNumber, { isLoading }] = useBindNumberMutation();
  const factoriesQuery = useGetFactoriesQuery({ orgId });
  const factories = useMemo(
    () => (factoriesQuery.data ?? []) as FactoryWithForwarding[],
    [factoriesQuery.data],
  );
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const factory of factories) {
      map.set(factory.id, factory.name);
    }
    return map;
  }, [factories]);

  const [selected, setSelected] = useState('');

  // The currently bound factory is preselected (§9.1).
  useEffect(() => {
    if (open) {
      setSelected(number.factoryId ?? '');
    }
  }, [open, number]);

  const { voiceAssistants, others } = useMemo(() => groupFactoriesByVoice(factories), [factories]);
  const currentName = number.factoryId ? nameById.get(number.factoryId) : undefined;
  const selectedName = selected ? nameById.get(selected) : undefined;
  const isMove = Boolean(number.factoryId && selected && selected !== number.factoryId);

  async function handleSubmit() {
    if (!selected) {
      return;
    }
    try {
      await bindNumber({ id: number.id, factoryId: selected }).unwrap();
      const name = selectedName ?? selected;
      toast.success(isMove ? `Number moved to ${name}.` : `Number bound to ${name}.`);
      onOpenChange(false);
    } catch (error) {
      const { status } = normalizeApiError(error);
      if (status === 503) {
        toast.error("Couldn't bind number. Couldn't reach the agent factory service. Try again.");
      } else {
        toast.error(mutationErrorMessage(error, "Couldn't bind number."));
      }
    }
  }

  const factoriesErrorStatus = factoriesQuery.isError
    ? normalizeApiError(factoriesQuery.error).status
    : undefined;

  function renderFactoryOption(factory: FactoryWithForwarding) {
    return (
      <SelectItem key={factory.id} value={factory.id}>
        {factory.name}
      </SelectItem>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isLoading) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Bind number to agent</DialogTitle>
          <DialogDescription className="sr-only">
            Bind {number.number} to an agent in this organization.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <p className="font-mono text-sm">{number.number}</p>
            {number.label ? <p className="text-sm text-muted-foreground">{number.label}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="bind-agent">Agent</Label>
            <Select
              value={selected}
              onValueChange={setSelected}
              disabled={factoriesQuery.isLoading || factoriesQuery.isError || isLoading}
            >
              <SelectTrigger id="bind-agent">
                <SelectValue
                  placeholder={factoriesQuery.isLoading ? 'Loading agents…' : 'Select an agent'}
                />
              </SelectTrigger>
              <SelectContent>
                {voiceAssistants.length > 0 ? (
                  <SelectGroup>
                    <SelectLabel>Voice assistants</SelectLabel>
                    {voiceAssistants.map(renderFactoryOption)}
                  </SelectGroup>
                ) : null}
                {voiceAssistants.length > 0 && others.length > 0 ? <SelectSeparator /> : null}
                {others.length > 0 ? (
                  <SelectGroup>
                    <SelectLabel>Other agents</SelectLabel>
                    {others.map(renderFactoryOption)}
                  </SelectGroup>
                ) : null}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Voice assistants can answer calls on this number. Binding moves the number from its
              current agent, if any.
            </p>
          </div>

          {currentName ? (
            <p className="text-sm text-muted-foreground">Currently bound to {currentName}.</p>
          ) : null}

          {isMove && currentName && selectedName ? (
            <BoundAgentNotice>
              This moves the number from {currentName} to {selectedName}.
            </BoundAgentNotice>
          ) : null}

          {factoriesQuery.isError ? (
            factoriesErrorStatus === 403 ? (
              <PolicyDeniedView />
            ) : (
              <div className="flex items-center justify-between gap-2 rounded-md border p-3">
                <span className="text-sm">Couldn't load agents.</span>
                <Button variant="outline" size="sm" onClick={() => void factoriesQuery.refetch()}>
                  Retry
                </Button>
              </div>
            )
          ) : null}

          {!factoriesQuery.isLoading && !factoriesQuery.isError && factories.length === 0 ? (
            <EmptyState
              title="No agents"
              description="Agents you own in this organization appear here. Ask an org manager for access to shared agents."
              className="py-4"
            />
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={
              !selected || isLoading || factoriesQuery.isLoading || factoriesErrorStatus === 403
            }
          >
            {isLoading ? <LoadingSpinner size="sm" className="mr-2" label="Binding" /> : null}
            {isLoading ? 'Binding…' : isMove ? 'Move number' : 'Bind number'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
